import React from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import {
  Users, Lightning, ChartBar, Bug,
  ChatCircleText, CalendarBlank,
} from '@phosphor-icons/react';
import {
  Select, SelectTrigger, SelectContent, SelectItem,
} from '../ui/select';

// Bulk SMS is NOT a standalone section anymore — it lives inside the Comms
// section (a button in CommsTab opens the Bulk SMS Center). So the nav shows
// a single "Comms" destination and highlights it for /admin/communications too.
const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: ChartBar, to: '/admin' },
  { id: 'events', label: 'Events', icon: CalendarBlank, to: '/admin/events' },
  { id: 'players', label: 'Members', icon: Users, to: '/admin/members' },
  { id: 'challenges', label: 'Challenges', icon: Lightning, to: '/admin?tab=challenges' },
  { id: 'comms', label: 'Comms', icon: ChatCircleText, to: '/admin?tab=comms' },
  { id: 'debug', label: 'Debug', icon: Bug, to: '/admin?tab=debug' },
];

// Map the current URL to the active admin section so the right pill is
// highlighted on every nested admin route (not just /admin).
export function activeAdminSection(pathname, tabParam) {
  if (pathname.startsWith('/admin/events')
      || pathname.startsWith('/admin/club-events')
      || pathname.startsWith('/admin/ladders')
      || pathname.startsWith('/tournaments/')
      || pathname.startsWith('/round-robins/')) return 'events';
  if (pathname.startsWith('/admin/members')) return 'players';
  // Bulk SMS pages now belong to the Comms section.
  if (pathname.startsWith('/admin/communications')) return 'comms';
  if (pathname === '/admin') return tabParam || 'dashboard';
  return tabParam || 'dashboard';
}

// Persistent Admin navigation. Rendered once by AppLayout on every admin
// route, so nested management/editor/creation pages all keep the nav.
// Desktop / wide tablet → horizontal pills. Mobile / narrow tablet → one
// compact "Admin: <Section>" dropdown so the bar never wraps/crowds.
export default function AdminNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const active = activeAdminSection(location.pathname, searchParams.get('tab'));
  const activeTab = TABS.find((t) => t.id === active) || TABS[0];
  const ActiveIcon = activeTab.icon;

  const go = (id) => {
    const t = TABS.find((x) => x.id === id);
    if (t) navigate(t.to);
  };

  return (
    <nav data-testid="admin-nav" aria-label="Admin sections">
      {/* Desktop / wide tablet — horizontal pills */}
      <div className="hidden md:flex flex-wrap gap-1 bg-[#f0ece3] p-1 rounded-xl">
        {TABS.map((t) => {
          const isActive = active === t.id;
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              aria-current={isActive ? 'page' : undefined}
              onClick={() => navigate(t.to)}
              className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium transition-all whitespace-nowrap ${
                isActive
                  ? 'bg-[#fbfaf8] text-[#015e3f] shadow-sm'
                  : 'text-[#6a766e] hover:text-[#013523]'
              }`}
            >
              <Icon size={14} weight={isActive ? 'fill' : 'regular'} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Mobile / narrow tablet — compact dropdown */}
      <div className="md:hidden">
        <Select value={active} onValueChange={go}>
          <SelectTrigger
            data-testid="admin-nav-select"
            aria-label="Admin section"
            className="h-11 w-full bg-[#f0ece3] border-[#e3dcd1] rounded-xl px-3"
          >
            <div className="flex items-center gap-2 text-[#013523]">
              <ActiveIcon size={17} weight="fill" className="text-[#015e3f]" />
              <span className="text-[13px] font-medium">
                <span className="text-[#6a766e]">Admin:</span> {activeTab.label}
              </span>
            </div>
          </SelectTrigger>
          <SelectContent data-testid="admin-nav-menu">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <SelectItem key={t.id} value={t.id} data-testid={`admin-nav-option-${t.id}`}>
                  <span className="flex items-center gap-2">
                    <Icon size={15} /> {t.label}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
    </nav>
  );
}
